import React from 'react';
import clsx from 'clsx';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import type { Equipamento } from '../../../../models';
import { SITUACAO_ATIVO_BADGE, SITUACAO_ATIVO_LABELS, TIPO_ATIVO_LABELS } from './ativos.definitions';
import { detalheSituacaoDoAtivo, situacaoDoAtivo, type ContextoAtivos, type PendenciaAtivo } from './ativos.utils';
import { normalizarPatrimonio } from '../../../../models/equipamento.utils';
import { fabricanteEModelo } from '../../../../utils/descreverEquipamentoResumo';

interface AtivoLinhaProps {
  ativo: Equipamento;
  pendencias: PendenciaAtivo[];
  contexto: ContextoAtivos;
  onSelect: (id: string) => void;
}

/** Linha da lista de Ativos: "RLMP042 | Macbook · Apple MacBook Pro | série", quem usa e a
 * Situação; ícone vermelho quando há pendência (mesmo aviso do alerta do topo). */
export default function AtivoLinha({ ativo, pendencias, contexto, onSelect }: AtivoLinhaProps) {
  const situacao = situacaoDoAtivo(ativo);
  const detalhe = detalheSituacaoDoAtivo(ativo, contexto);
  const colaborador = ativo.colaboradorAtualId ? contexto.nomeColaboradorPorId.get(ativo.colaboradorAtualId) : null;
  const projeto = ativo.projetoClienteId ? contexto.nomeClientePorId.get(ativo.projetoClienteId) : null;
  const descricao = [TIPO_ATIVO_LABELS[ativo.tipo] ?? ativo.tipo, fabricanteEModelo(ativo.fabricante, ativo.modelo)]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      onClick={() => onSelect(ativo.id)}
      className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-gray-200 bg-white px-4 py-3 text-left hover:border-indigo-300"
    >
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 font-montserrat text-gray-900">
          {pendencias.length > 0 && (
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-500" title={pendencias.map((p) => p.mensagem).join('\n')} />
          )}
          <span className="font-bold">{normalizarPatrimonio(ativo.patrimonio) ?? 'Sem patrimônio'}</span>
          {descricao && (
            <>
              <span className="text-gray-300">|</span>
              <span className="truncate text-sm text-gray-600">{descricao}</span>
            </>
          )}
          {ativo.nSerie && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-sm text-gray-600">{ativo.nSerie}</span>
            </>
          )}
        </p>
        <p className="text-xs text-gray-500">
          {[colaborador ? `Com ${colaborador}` : null, projeto ? `Projeto: ${projeto}` : null].filter(Boolean).join(' · ') || '—'}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {/* Situação primeiro, complemento depois ("Devolvido ao Cliente · SWAP") — mesma ordem da ficha. */}
        <span className={clsx('rounded-full px-2 py-0.5 text-xs font-semibold', SITUACAO_ATIVO_BADGE[situacao])}>
          {SITUACAO_ATIVO_LABELS[situacao]}
        </span>
        {detalhe && <span className="text-xs text-gray-500">{detalhe}</span>}
      </div>
    </button>
  );
}
